import { useReducer } from 'react';
import { createContext } from 'use-context-selector';
import type { FC } from 'react';
import type { Customer } from '../types/customer';
import { OrderList } from 'src/types/orderList';
import orderApi from '../api/OrderApi';
import { CompositeFilterDescriptor, State as ODataState } from '@progress/kendo-data-query';
import { LOCAL_STORAGE_KEYS } from 'src/constants';
import { Logger } from 'src/utils/logger';
import { ContextProviderProps } from 'src/types/contextProviderProps';
import { ShipTo } from 'src/types/orderHeaderDropdowns';

const createEmptyOrderList = (): OrderList => ({
  loading: false,
  orders: [],
  total: 0,
  error: null,
});

export interface CustomerContextValue {
  customer: Customer;
  saveCustomer: (customer: Customer) => void;
  deleteUnsubmittedOrder: (orderId: number) => void;
  getUnsubmittedOrders: (dataState: ODataState) => Promise<void>;
  getOpenOrders: (dataState: ODataState, searchText: string) => Promise<void>;
  setPreferredShipTo(shipTo: ShipTo): void;
  SetOpenOrderFilters(filters: CompositeFilterDescriptor[]): void;
}

type GetUnsubmittedOrdersAction = {
  type: 'GetUnsubmittedOrders';
  payload: {
    orderList: OrderList;
  };
};

type GetOpenOrdersAction = {
  type: 'GetOpenOrders';
  payload: {
    orderList: OrderList;
  };
};

type SetCustomerAction = {
  type: 'SetCustomer';
  payload: {
    customer: Customer;
  };
};

type DeleteUnsubmittedOrderAction = {
  type: 'DeleteUnsubmittedOrder';
  payload: {
    orderId: number;
  };
};

type SetOpenOrderFiltersAction = {
  type: 'SetOpenOrderFilters';
  payload: {
    filters: CompositeFilterDescriptor[];
  };
};

type Action = GetUnsubmittedOrdersAction | GetOpenOrdersAction | SetCustomerAction | DeleteUnsubmittedOrderAction | SetOpenOrderFiltersAction;

const initialCustomer: Customer = {
  name: 'NO CUSTOMER',
  number: 0,
  numberString: '0',
  avatarPath: '/static/avatars/anon.png',
  openOrders: createEmptyOrderList(),
  unsubmittedOrders: createEmptyOrderList(),
  poRequired: false,
  shipTos: [],
  openOrderFilters: [],
  defaultShipTo: '',
  selectedShipToNumber: '',
  selectedShipToTermCode: '',
  selectedShipToWarehouse: '',
  // Add any other required Customer properties
};

export const restoreCustomer = (): Customer | null => {
  let customer: Customer = null;
  try {
    const storedData: string | null = window.localStorage.getItem(LOCAL_STORAGE_KEYS.SelectedCustomer);

    if (storedData) {
      customer = JSON.parse(storedData);

      // Ensure complete OrderList structure
      customer.openOrders = customer.openOrders || createEmptyOrderList();
      customer.unsubmittedOrders = customer.unsubmittedOrders || createEmptyOrderList();
      customer.openOrderFilters = customer.openOrderFilters || [];

      // Ensure OrderList properties are complete
      if (typeof customer.openOrders.loading === 'undefined') {
        customer.openOrders.loading = false;
      }
      if (typeof customer.openOrders.error === 'undefined') {
        customer.openOrders.error = null;
      }
      if (typeof customer.unsubmittedOrders.loading === 'undefined') {
        customer.unsubmittedOrders.loading = false;
      }
      if (typeof customer.unsubmittedOrders.error === 'undefined') {
        customer.unsubmittedOrders.error = null;
      }
    } else {
      customer = initialCustomer;
    }
  } catch (err) {
    Logger.error(err);
    customer = initialCustomer; // Fallback to initial customer if parsing fails
  }

  const shipTo = getLocalStorageCustomerShipTo(customer.numberString);

  if (shipTo) {
    customer.selectedShipToNumber = shipTo.shipNo;
    customer.selectedShipToTermCode = shipTo.termCode;
    customer.selectedShipToWarehouse = shipTo.warehouseCode;
  }

  return customer;
};

export const storeCustomer = (customer: Customer): void => {
  window.localStorage.setItem(LOCAL_STORAGE_KEYS.SelectedCustomer, JSON.stringify(customer));
};

const CustomerContext = createContext<CustomerContextValue>({
  customer: initialCustomer,
  saveCustomer: () => {},
  getUnsubmittedOrders: () => Promise.resolve(),
  getOpenOrders: () => Promise.resolve(),
  deleteUnsubmittedOrder: () => Promise.resolve(),
  setPreferredShipTo: () => {},
  SetOpenOrderFilters: () => {},
});

const handlers: Record<string, (state: Customer, action: Action) => Customer> = {
  GetUnsubmittedOrders: (state: Customer, action: GetUnsubmittedOrdersAction): Customer => {
    const { orderList } = action.payload;
    orderList.loading = orderList.loading;

    return {
      ...state,
      unsubmittedOrders: orderList,
    };
  },
  GetOpenOrders: (state: Customer, action: GetOpenOrdersAction): Customer => {
    const { orderList } = action.payload;
    orderList.loading = orderList.loading;

    return {
      ...state,
      openOrders: orderList,
      openOrderFilters: state.openOrderFilters,
    };
  },
  DeleteUnsubmittedOrder: (state: Customer, action: DeleteUnsubmittedOrderAction): Customer => {
    const { orderId } = action.payload;
    const orders = [...state.unsubmittedOrders.orders];

    return {
      ...state,
      unsubmittedOrders: { orders: orders.filter((o) => o.sfaOrderId != orderId), total: state.unsubmittedOrders.total - 1 },
    };
  },
  SetCustomer: (state: Customer, action: SetCustomerAction): Customer => {
    const newCustomer = action.payload.customer;
    return {
      ...state,
      ...newCustomer,
      // Ensure these properties always exist with proper defaults
      openOrders: newCustomer.openOrders || { loading: false, orders: [], total: 0, error: null },
      unsubmittedOrders: newCustomer.unsubmittedOrders || { loading: false, orders: [], total: 0, error: null },
      openOrderFilters: newCustomer.openOrderFilters || state.openOrderFilters || [],
    };
  },

  SetOpenOrderFilters: (state: Customer, action: SetOpenOrderFiltersAction): Customer => {
    const { filters } = action.payload;
    return {
      ...state,
      openOrderFilters: filters,
    };
  },
};

const reducer = (state: Customer, action: Action): Customer => (handlers[action.type] ? handlers[action.type](state, action) : state);

export const CustomerProvider: FC<ContextProviderProps> = (props) => {
  const { children } = props;
  const [customer, dispatch] = useReducer(reducer, restoreCustomer() || initialCustomer);

  const saveCustomer = (updatedCustomer: Customer): void => {
    if (!updatedCustomer?.selectedShipToNumber) {
      updatedCustomer.selectedShipToNumber = updatedCustomer.defaultShipTo;
    }

    dispatch({
      type: 'SetCustomer',
      payload: { customer: updatedCustomer },
    });

    storeCustomer(updatedCustomer);
  };

  const setPreferredShipTo = (shipTo: ShipTo): void => {
    setLocalStorageCustomerShipTo(customer.numberString, shipTo);
    saveCustomer({
      ...customer,
      selectedShipToNumber: shipTo.shipNo,
      selectedShipToTermCode: shipTo.termCode,
      selectedShipToWarehouse: shipTo.warehouseCode,
    });
  };

  const getUnsubmittedOrders = async (dataState: ODataState): Promise<void> => {
    dispatch({
      type: 'GetUnsubmittedOrders',
      payload: {
        orderList: { orders: [], loading: true, error: null, total: 0 },
      },
    });

    orderApi.getUnsubmittedOrders(dataState).then((data) => {
      dispatch({
        type: 'GetUnsubmittedOrders',
        payload: {
          orderList: { orders: data.value.map((o) => o), loading: false, error: null, total: data['@odata.count'] },
        },
      });
    });
  }; // No dependencies - uses only stable dispatch and external API

  const getOpenOrders = async (dataState: ODataState, searchText: string): Promise<void> => {
    dispatch({
      type: 'GetOpenOrders',
      payload: {
        orderList: { orders: [], loading: true, error: null, total: 0 },
      },
    });

    const data = await orderApi.getOpenOrders(dataState, searchText, customer.numberString);
    const orders = data.value.map((order) => order);

    dispatch({
      type: 'GetOpenOrders',
      payload: {
        orderList: { orders, loading: false, error: null, total: data['@odata.count'] },
      },
    });
  }; // Depends on customer.numberString for the API call

  const deleteUnsubmittedOrder = async (orderId: number): Promise<void> => {
    orderApi.deleteUnsubmittedOrder(orderId).then(() => {
      dispatch({
        type: 'DeleteUnsubmittedOrder',
        payload: {
          orderId: orderId,
        },
      });
    });
  }; // No dependencies - uses only stable dispatch and external API

  const SetOpenOrderFilters = async (filters: CompositeFilterDescriptor[]): Promise<void> => {
    dispatch({
      type: 'SetOpenOrderFilters',
      payload: { filters },
    });
  }; // No dependencies - uses only stable dispatch

  const contextValue = {
    customer,
    saveCustomer,
    getOpenOrders,
    getUnsubmittedOrders,
    deleteUnsubmittedOrder,
    setPreferredShipTo,
    SetOpenOrderFilters,
  };

  return <CustomerContext.Provider value={contextValue}>{children}</CustomerContext.Provider>;
};

export default CustomerContext;

/**
 * Set the preferred ship to for a customer in local storage
 * The value stored per each customer number is a serialzed ShipTo object
 * @param customerNumber
 * @param shipTo
 */
function setLocalStorageCustomerShipTo(customerNumber: string, shipTo: ShipTo): void {
  const storedData: string | null = window.localStorage.getItem(LOCAL_STORAGE_KEYS.CustomerShipTo);

  if (storedData) {
    //key/object exists, add to it
    const data = JSON.parse(storedData);
    data[customerNumber] = JSON.stringify(shipTo);
    window.localStorage.setItem(LOCAL_STORAGE_KEYS.CustomerShipTo, JSON.stringify(data));
  } else {
    //key/object does not exist, create it
    const data = {};
    data[customerNumber] = JSON.stringify(shipTo);
    window.localStorage.setItem(LOCAL_STORAGE_KEYS.CustomerShipTo, JSON.stringify(data));
  }
}

/**
 * Get the preferred ship to for a customer from local storage
 * The value stored per each customer number is a serialzed ShipTo object
 * @param customerNumber
 * @returns
 */
export function getLocalStorageCustomerShipTo(customerNumber: string): ShipTo | null {
  const storedData: string | null = window.localStorage.getItem(LOCAL_STORAGE_KEYS.CustomerShipTo);
  if (storedData) {
    const outerData = JSON.parse(storedData);
    const shipToString = outerData[customerNumber]; // This is still a JSON string
    if (shipToString) {
      const shipToData: ShipTo = JSON.parse(shipToString);
      return shipToData;
    }
  }
  return null;
}
